import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plane, ArrowRight, Luggage, AlertTriangle, ChevronDown, ChevronUp, Check, Mail, Phone, User, Calendar, Clock, Utensils, Wifi, Tv, Coffee, LogIn, LogOut, Wallet, Loader } from 'lucide-react';
// Sprint-7 · `airports` from data/mock is no longer needed — replaced by
// authoritative `airportLookup` util backed by 5,379-entry airports.json.
import { isInternationalRoute, getAirportInfo } from '../utils/airportLookup';
import BottomNav from '../components/BottomNav';
import Footer from '../components/Footer';
import FlightDetailsCard from '../components/FlightDetailsCard';
import FareRulesPanel from '../components/FareRulesPanel';
import { getFareTypeInfo } from '../utils/fareTypeMapping';
import { validateName, validateEmail, validatePhone, validatePassport, validateDOB, validatePassportExpiry, getTitleOptions, toHTMLDateInputValue } from '../utils/passengerValidation';
import { enrichOfflineFlight } from '../utils/airportMapper';
import PhoneInput from '../components/ui/PhoneInput';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import { resolveTripjackSegmentBaggage } from '../utils/tripjackTripBaggage';
import { getUserFacingBookingId } from '../utils/userFacingBookingId';
import SessionExpiryChip from '../components/SessionExpiryChip';
import ReviewMessagesBanner from '../components/ReviewMessagesBanner';
import useSessionExpiry from '../hooks/useSessionExpiry';
import { useAuth } from '../context/AuthContext';
import { useAgentAuth } from '../context/AgentAuthContext';
import MobileOTPLogin from '../components/MobileOTPLogin';
import AgentBalanceBadge from '../components/agent/AgentBalanceBadge';
import FareRepriceModal from '../components/FareRepriceModal';
import HoldBookingToggle from '../components/HoldBookingToggle';
import axios from 'axios';
import { deriveDisplayFare } from '../utils/deriveDisplayFare';
import { deriveCommissionFare, resolveCommissionPreview } from '../utils/deriveCommissionFare';
import FareCommissionRows from '../components/FareCommissionRows';
import {
  fareAlertMatchesAcceptance,
  resolveAcceptedNewTotal,
} from '../utils/fareAlertAcceptance';
import { tripjackSessionBookingId } from '../utils/tripjackSessionBookingId';
import { bookingSessionHeaders } from '../utils/bookingSessionCorrelation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Tripjack /review + pax gate need a supplier tag; RT/combo payloads often only set source on nested legs. */
function resolveBookingPageSupplierSource(flightData, bookingType) {
  if (!flightData || typeof flightData !== 'object') return '';
  const root = String(flightData.source || '').toLowerCase();
  if (root) return root;
  const bt = bookingType || flightData.type || '';
  if (bt === 'roundtrip') {
    const o = String(flightData.onward?.source || '').toLowerCase();
    const r = String(flightData.return?.source || '').toLowerCase();
    if (o === 'riya' || r === 'riya') return 'riya';
    if (o === 'offline' || r === 'offline') return 'offline';
    return o || r || 'tripjack';
  }
  if (bt === 'combo') {
    return String(flightData.flight?.source || '').toLowerCase() || 'tripjack';
  }
  return '';
}

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, getSavedPassengers } = useAuth();
  const { agent, isAuthenticated: isAgentAuthenticated, refreshAgentBalances } = useAgentAuth();
  
  useEffect(() => {
    if (!agent?.id) return;
    refreshAgentBalances();
  }, [agent?.id]);
  // Get booking parameters
  const bookingType = searchParams.get('multiCity') === '1'
    ? 'multicity'
    : searchParams.get('type');
  const priceId = searchParams.get('priceId');
  const onwardPriceId = searchParams.get('onwardPriceId');
  const returnPriceId = searchParams.get('returnPriceId');
  
  // Get passenger counts from URL
  const numAdults = parseInt(searchParams.get('adults')) || 1;
  const numChildren = parseInt(searchParams.get('children')) || 0;
  const numInfants = parseInt(searchParams.get('infants')) || 0;

  // Flight data
  const [flightData, setFlightData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [error, setError] = useState(null);
  const [airlineLogos, setAirlineLogos] = useState({});
  
  // Auth states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [savedPassengers, setSavedPassengers] = useState([]);
  
  // Searchable dropdown states
  const [activePassengerIndex, setActivePassengerIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTravellerIds, setSelectedTravellerIds] = useState([]);
  
  // Passenger form - Dynamic array
  const [passengers, setPassengers] = useState([]);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstDetails, setGstDetails] = useState({
    gstNumber: '',
    registeredName: '',
    email: '',
    mobile: '',
    address: ''
  });

  // G3 · Emergency contact (only when /review.iecr=true). We surface the
  // requirement after the review fetch and conditionally validate before
  // payment. The block ships to backend as `contactInfo` on /book.
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [emergencyContactRequired, setEmergencyContactRequired] = useState(false);

  // T0 · PAN required (Tripjack v2.0.2 — `/review.conditions.ipa=true`).
  // Surfaced after review fetch, blocks Proceed until every adult has a
  // valid 10-char PAN. Sent to backend as `traveller.pan`.
  const [panRequired, setPanRequired] = useState(false);
  // Sprint H1 · Hold booking option (Tripjack `conditions.isBA=true`).
  // Decides whether to call /book in instant mode (with paymentInfos) or
  // hold mode (without paymentInfos). User picks via HoldBookingToggle.
  const [holdAllowed, setHoldAllowed] = useState(false);
  const [bookingMode, setBookingMode] = useState('');  // 'instant' | 'hold' (set by toggle)
  // Audit-P0 R-1 (May-2026): cache the entire `form_conditions` object
  // returned by /review so the JSX render path can reactively show the
  // adult-DOB field for fares with `pcs.dobe=true`, surface PAN, doc-id,
  // emergency-contact requirements etc. without re-running a side effect.
  // First populated on the first /review call inside handleBooking; the
  // form re-renders, the user fills in the missing field, and retries.
  const [formConditions, setFormConditions] = useState(null);
  
  // Countdown timer states
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [fareExpired, setFareExpired] = useState(false);
  const [fareRepriceOpen, setFareRepriceOpen] = useState(false);
  const [fareRepriceDetail, setFareRepriceDetail] = useState(null);
  const [fareRepriceAttempt, setFareRepriceAttempt] = useState(0);
  const [fareRepricePriceIds, setFareRepricePriceIds] = useState([]);
  const [repriceContinueHint, setRepriceContinueHint] = useState('');
  /** U1 (Audit follow-up) — Hard latch when supplier exhausts reprice
   *  attempts. Disables every payment / continue CTA on this page so the
   *  user can't proceed with a stale priceId. Only cleared by a new search. */
  const [repriceExhausted, setRepriceExhausted] = useState(false);
  /** U2 — `session_expires_at` driven hard-block at supplier TTL (Audit §6.6). */
  const [sessionExpired, setSessionExpired] = useState(false);
  // Cheap ref-mirror so the JSX gate can know "is there a TTL at all" without
  // an extra hook call inside the render closure.
  const sessionExpiryStateRef = useRef({ hasSupplierTtl: false });
  /** Supplier total (₹) after last accepted soft-reprice — absolute, never compounded. */
  const [fareRepriceOverrideTotal, setFareRepriceOverrideTotal] = useState(null);

  // ─── Pax-aware total fare computation ───────────────────────────────
  // Critical: the per-flight `price` field returned by suppliers is the
  // adult fare (per-pax), NOT the cart total. Multiply by pax count and
  // sum across types. Falls back gracefully when `fares` is missing.
  // NOTE: keep in sync with backend validator in routes/agent_booking._validate_total.
  const rawFlightTotal = useCallback((flight) => {
    if (!flight) return 0;
    const fares = flight.fares || {};
    const adultFare = Number(fares.adult ?? flight.price ?? 0) || 0;
    const childFare = Number(fares.child ?? fares.adult ?? flight.price ?? 0) || 0;
    const infantFare = Number(fares.infant ?? 0) || 0;
    const total = (numAdults * adultFare) + (numChildren * childFare) + (numInfants * infantFare);
    const supplierTotal = Number(flight.totalFare ?? flight.totalPrice ?? 0) || 0;
    return Math.max(total, supplierTotal);
  }, [numAdults, numChildren, numInfants]);

  const computeFlightTotal = useCallback((flight) => {
    if (!flight) return 0;
    const base = rawFlightTotal(flight);
    const ov = fareRepriceOverrideTotal;
    if (ov == null || !Number.isFinite(ov) || ov <= 0) {
      return Math.round(base * 100) / 100;
    }
    const absTotal = Math.round(ov * 100) / 100;
    if (bookingType === 'multicity' && Array.isArray(flightData?.legs) && flightData.legs.length) {
      const raws = flightData.legs.map((x) => rawFlightTotal(x.flight));
      const sumRaw = raws.reduce((a, b) => a + b, 0) || 1;
      const idx = flightData.legs.findIndex((x) => x.flight === flight);
      if (idx >= 0) {
        return Math.round(absTotal * (raws[idx] / sumRaw) * 100) / 100;
      }
    }
    if (bookingType === 'roundtrip' && flightData?.onward && flightData?.return) {
      const ob = rawFlightTotal(flightData.onward);
      const rb = rawFlightTotal(flightData.return);
      const sumLeg = ob + rb || 1;
      if (flight === flightData.onward) {
        return Math.round(absTotal * (ob / sumLeg) * 100) / 100;
      }
      if (flight === flightData.return) {
        return Math.round(absTotal * (rb / sumLeg) * 100) / 100;
      }
    }
    if (bookingType === 'oneway' && flight === flightData) {
      return absTotal;
    }
    if (bookingType === 'combo' && flight === flightData?.flight) {
      return absTotal;
    }
    return Math.round(base * 100) / 100;
  }, [rawFlightTotal, fareRepriceOverrideTotal, bookingType, flightData]);

  const [searchTimestamp, setSearchTimestamp] = useState(null);
  // Sprint-3 · /review on mount — pre-fetch per-fare requirements as soon as
  // we know the priceId, rather than waiting for Proceed click. Eliminates
  // the 5s race condition AND lets the form fields adapt before the user
  // starts typing (no jarring re-render mid-form).
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [cachedReviewData, setCachedReviewData] = useState(null);
  const [commissionPreview, setCommissionPreview] = useState(null);
  /** Tripjack Air Review `bookingId` from POST /tripjack/review — gates pax + drives FareRulesPanel (same as ReviewBooking). */
  const [tripjackBookingIdFromReview, setTripjackBookingIdFromReview] = useState(null);
  /** Normalised fare rules from POST /tripjack/review when Tripjack embeds totalPriceList[].fareRuleInformation (Audit #17). */
  const [embeddedFareRulesFromReview, setEmbeddedFareRulesFromReview] = useState(null);
  /** Increment to re-run the mount /review effect (Retry fare check). */
  const [reviewRefetchNonce, setReviewRefetchNonce] = useState(0);
  const pendingFareAcceptanceRef = useRef(null);

  const handleBookingPageReprice = useCallback((ev) => {
    if (ev?.type === 'still_changed' && ev.detail) {
      setFareRepriceDetail(ev.detail);
      setFareRepriceOpen(true);
      setFareRepriceAttempt((prev) => {
        const fromBackend = ev.detail.reprice_attempt ?? ev.detail.repriceAttempt;
        if (fromBackend != null && fromBackend !== '') {
          const n = Number(fromBackend);
          if (!Number.isNaN(n)) return Math.max(prev, n);
        }
        return prev + 1;
      });
      return;
    }
    if (ev?.type === 'exhausted' && ev.detail) {
      setFareRepriceDetail(ev.detail);
      setFareRepriceOpen(true);
      setRepriceExhausted(true);
      return;
    }
    if (ev?.type === 'ok' && ev.payload) {
      const out = ev.payload;
      let prevMarked = fareRepriceOverrideTotal;
      if ((prevMarked == null || prevMarked <= 0) && flightData) {
        if (bookingType === 'oneway') {
          prevMarked = computeFlightTotal(flightData);
        } else if (bookingType === 'roundtrip') {
          prevMarked =
            computeFlightTotal(flightData?.onward) + computeFlightTotal(flightData?.return);
        } else if (bookingType === 'combo') {
          prevMarked = computeFlightTotal(flightData?.flight);
        }
      }
      const acceptedTotal = resolveAcceptedNewTotal(out, ev.acceptance, {
        previousMarkedUpTotal: prevMarked,
      });
      if (acceptedTotal != null) {
        setFareRepriceOverrideTotal(acceptedTotal);
      }
      pendingFareAcceptanceRef.current = {
        ...ev.acceptance,
        user_id: agent?.id || agent?.agent_id || null,
      };
      setCachedReviewData(out.data ?? null);
      if (out.form_conditions) {
        setFormConditions(out.form_conditions);
      }
      if (out.tripjack_booking_id) {
        setTripjackBookingIdFromReview(String(out.tripjack_booking_id));
      }
      setEmbeddedFareRulesFromReview(out.embedded_fare_rules_normalized ?? null);
      setFareRepriceAttempt((prev) => {
        const next = out.reprice_attempt;
        if (next == null || next === '') return prev;
        const n = Number(next);
        if (Number.isNaN(n)) return prev;
        return Math.max(prev, n);
      });
      setFareRepriceOpen(false);
      setFareRepriceDetail(null);
      setRepriceContinueHint('Fare refreshed — tap “Continue to Add-Ons” again to continue with the updated price.');
      setError(null);
    }
  }, [agent]);

  // Sprint Feb-2026 · Markup quote fallback for deeplink users.
  // The happy path reads markup directly from the search-response
  // `fareBreakdown.*.markup` (Tripjack) or `_markup_amount_*` (Riya) —
  // baked in at /search-all. But a user who lands on /booking via
  // deeplink (`?priceId=…`) has no in-flight search payload, so the
  // Service Charge would never render. Quote it post-mount in that case.
  // NOTE: hook MUST live above early returns to satisfy React rules-of-hooks.
  const [_quotedServiceCharge, _setQuotedServiceCharge] = useState(0);

  // U2 — Subscribe to supplier session expiry. Calls onExpire which trips the
  // `sessionExpired` hard-block above. The ref mirror lets the render
  // closure know "is there a TTL at all" without re-invoking the hook there.
  const _sessionExpiry = useSessionExpiry(formConditions);
  useEffect(() => {
    sessionExpiryStateRef.current = {
      hasSupplierTtl: !!_sessionExpiry.hasSupplierTtl,
    };
  }, [_sessionExpiry.hasSupplierTtl]);
  useEffect(() => {
    if (_sessionExpiry.isExpired && !sessionExpired) {
      setSessionExpired(true);
    }
  }, [_sessionExpiry.isExpired, sessionExpired]);

  // Markup quote effect — fires when flightData lands and the search-response
  // didn't already carry a per-pax markup breakdown. Re-derives base/tax to
  // anchor the quote, then calls /api/markup/quote (best-effort, never blocks).
  useEffect(() => {
    if (!flightData) return;

    const _fd = deriveDisplayFare({
      flightData,
      bookingType,
      numAdults,
      numChildren,
      numInfants,
    });
    if (_fd.serviceCharge > 0) return; // happy path covered already
    if (!_fd.hasFareBreakdown) return;
    const supplierTotal = Math.round(_fd.supplierNetForQuote || 0);
    if (supplierTotal <= 0) return;

    let cancelled = false;
    (async () => {
      try {
        const _df =
          bookingType === 'oneway'
            ? flightData
            : bookingType === 'roundtrip'
              ? flightData.onward
              : bookingType === 'combo'
                ? flightData.flight
                : bookingType === 'multicity' && flightData?.legs?.[0]?.flight
                  ? flightData.legs[0].flight
                  : flightData;
        const resp = await axios.post(`${BACKEND_URL}/api/markup/quote`, {
          user_type: isAgentAuthenticated ? 'agent' : 'b2c',
          agent_id: agent?.id || agent?.agent_id || null,
          airline_code: _df?.airlineCode || null,
          route: _df?.from && _df?.to ? `${_df.from}-${_df.to}` : null,
          travel_class: _df?.classOfBooking || null,
          supplier_total: supplierTotal,
          num_adults: numAdults,
          num_children: numChildren,
          num_infants: numInfants,
        });
        if (!cancelled && resp.data?.matched && resp.data.amount > 0) {
          _setQuotedServiceCharge(Math.round(resp.data.amount));
        }
      } catch (_e) {
        // Quote is best-effort — never block booking on a markup miss.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightData, bookingType, numAdults, numChildren, numInfants, isAgentAuthenticated, agent?.id]);

  
  // Helper function to check if flight is international
  // Sprint-3 · SSOT audit (Feb-2026): now uses authoritative airports.json
  // (5,379 IATA codes with countryCode) instead of the 14-entry mock list.
  // Previously, unknown IATAs were silently treated as domestic, causing
  // wrong passport/DOB rules to be applied for valid international fares.
  const checkIsInternational = (flightData) => {
    if (!flightData) return false;
    const fromCode = flightData.from || flightData.route?.from;
    const toCode = flightData.to || flightData.route?.to;
    return isInternationalRoute(fromCode, toCode);
  };
  
  useEffect(() => {
    // Sprint-2 · Multi-city funnel — when navigated with `?multiCity=1`,
    // the selected legs are stored under `multiCitySelection` (set by
    // MultiCitySearchResults). Synthesize a booking-shaped flightData
    // object so the rest of this page renders the legs correctly.
    if (bookingType === 'multicity') {
      const mcRaw = localStorage.getItem('multiCitySelection');
      if (mcRaw) {
        try {
          const mc = JSON.parse(mcRaw);
          const legs = mc.selectedLegs || [];
          if (legs.length > 0) {
            const totalPrice = legs.reduce(
              (sum, x) => sum + Number(x.flight?.price || x.flight?.totalFare || 0),
              0
            );
            let mcSource = 'tripjack';
            for (const x of legs) {
              const s = String(x?.flight?.source || '').toLowerCase();
              if (s === 'riya') {
                mcSource = 'riya';
                break;
              }
              if (s === 'offline') {
                mcSource = 'offline';
                break;
              }
            }
            const synthFlightData = {
              source: mcSource,
              bookingType: 'multicity',
              priceIds: mc.priceIds || legs.map((x) => x.flight?.priceId).filter(Boolean),
              legs,                                // [{leg, flight}]
              from: legs[0]?.leg?.from,
              to: legs[legs.length - 1]?.leg?.to,
              price: totalPrice,
              totalFare: totalPrice,
              totalPrice,
              airlineName: legs[0]?.flight?.airlineName,
              airlineCode: legs[0]?.flight?.airlineCode,
            };
            setFlightData(synthFlightData);
            setFareRepriceOverrideTotal(null);
            const storedTimestamp = localStorage.getItem('searchTimestamp');
            if (storedTimestamp) {
              setSearchTimestamp(parseInt(storedTimestamp));
            } else {
              const now = Date.now();
              localStorage.setItem('searchTimestamp', now.toString());
              setSearchTimestamp(now);
            }
            return;
          }
        } catch (e) {
          console.warn('[multicity] failed to parse multiCitySelection:', e);
        }
      }
      // No valid multi-city selection → user landed here directly. Bail.
      setError('No multi-city selection found. Please search and select flights again.');
      return;
    }

    const storedData = localStorage.getItem('selectedFlight');
    const storedTimestamp = localStorage.getItem('searchTimestamp');
    
    if (storedData) {
      try {
        let parsedData = JSON.parse(storedData);

        // Stamp root `source` when missing (RoundTrip / Intl combo only set leg `source`).
        if (!parsedData.source) {
          const bt = parsedData.type || bookingType || '';
          if (bt === 'roundtrip') {
            const o = parsedData.onward?.source;
            const r = parsedData.return?.source;
            if (o === 'riya' || r === 'riya') parsedData.source = 'riya';
            else if (o === 'offline' || r === 'offline') parsedData.source = 'offline';
            else parsedData.source = o || r || 'tripjack';
          } else if (bt === 'combo' && parsedData.flight) {
            parsedData.source = parsedData.flight.source || 'tripjack';
          }
        }
        
        // Enrich offline flight with airport data
        if (parsedData.source === 'offline') {
          console.log('🎫 Enriching offline flight data');
          parsedData = enrichOfflineFlight(parsedData);
        }
        
        // Diagnostic — confirm Riya fare-lock fields survived from SearchResults.
        if (parsedData.source === 'riya') {
          console.log('[riya] BookingPage flightData:', {
            riyaTrackId: parsedData.riyaTrackId ? `${String(parsedData.riyaTrackId).slice(0, 30)}…` : null,
            riyaFlightId: parsedData.riyaFlightId,
            riyaBaseAmount: parsedData.riyaBaseAmount,
            riyaGrossAmount: parsedData.riyaGrossAmount,
            riyaSearchedAt: parsedData.riyaSearchedAt,
            price: parsedData.price,
          });
        }
        
        setFlightData(parsedData);
        setFareRepriceOverrideTotal(null);

        // Set search timestamp (30 min countdown)
        if (storedTimestamp) {
          setSearchTimestamp(parseInt(storedTimestamp));
        } else {
          // If no timestamp, set current time
          const now = Date.now();
          localStorage.setItem('searchTimestamp', now.toString());
          setSearchTimestamp(now);
        }
        
        // Fetch airline logos
        const airlineCodes = new Set();
        if (parsedData.airlineCode) airlineCodes.add(parsedData.airlineCode);
        if (parsedData.onward?.airlineCode) airlineCodes.add(parsedData.onward.airlineCode);
        if (parsedData.return?.airlineCode) airlineCodes.add(parsedData.return.airlineCode);
        if (parsedData.flight?.airlineCode) airlineCodes.add(parsedData.flight.airlineCode);
        
        // Fetch logos for segments too
        if (parsedData.segments) {
          parsedData.segments.forEach(seg => {
            if (seg.fD?.aI?.code) airlineCodes.add(seg.fD.aI.code);
          });
        }
        if (parsedData.onward?.segments) {
          parsedData.onward.segments.forEach(seg => {
            if (seg.fD?.aI?.code) airlineCodes.add(seg.fD.aI.code);
          });
        }
        if (parsedData.return?.segments) {
          parsedData.return.segments.forEach(seg => {
            if (seg.fD?.aI?.code) airlineCodes.add(seg.fD.aI.code);
          });
        }
        
        if (airlineCodes.size > 0) {
          fetchAirlineLogos(Array.from(airlineCodes))
            .then(data => setAirlineLogos(prev => ({ ...prev, ...data })))
            .catch(err => console.error('Airline logo fetch error:', err));
        }
      } catch (e) {
        setError('Invalid flight data');
      }
    } else {
      setError('No flight selected');
    }
  }, [bookingType]);

  // Sprint-3 · Pre-fetch Tripjack /review on mount (was previously called
  // inside handleBooking with a 5s race). Surfacing form_conditions early:
  //   • lets the form adapt fields (DOB / passport / PAN) before the user
  //     fills them, avoiding mid-form re-renders
  //   • lets us surface gst_mandatory / hold_allowed / emergency_contact
  //     requirements as soon as the page is ready
  //   • removes the 5s timeout — handleBooking still re-validates against
  //     fresh /review for fare integrity, but UX no longer races.
  // Only Tripjack flights need this; Riya + offline have their own paths.
  useEffect(() => {
    if (!flightData) return;
    const src = resolveBookingPageSupplierSource(flightData, bookingType);
    if (src !== 'tripjack') return;
    if (bookingType === 'multicity') return;  // multi-city has no /review yet
    const ids = bookingType === 'roundtrip'
      ? [onwardPriceId, returnPriceId].filter(Boolean)
      : [priceId].filter(Boolean);
    if (ids.length === 0) return;

    let cancelled = false;
    setReviewLoading(true);
    setReviewError(null);
    setTripjackBookingIdFromReview(null);
    setEmbeddedFareRulesFromReview(null);
    const mountAcceptance = pendingFareAcceptanceRef.current;
    axios.post(`${API}/tripjack/review`, {
      priceIds: ids,
      ...(mountAcceptance ? { acceptance: mountAcceptance } : {}),
    }, { headers: { ...bookingSessionHeaders() } })
      .then(res => {
        if (cancelled) return;
        if (res.data?.success) {
          const fc = res.data.form_conditions || null;
          setFormConditions(fc);
          const rawBody = res.data.data != null ? res.data.data : res.data;
          const bid =
            (rawBody && typeof rawBody === 'object'
              ? (rawBody.bookingId || rawBody.orderId)
              : null) || res.data.bookingId || res.data.orderId;
          setTripjackBookingIdFromReview(bid ? String(bid) : null);
          setCachedReviewData(res.data.data || null);
          setCommissionPreview(res.data.commission_preview ?? null);
          setEmbeddedFareRulesFromReview(
            res.data.embedded_fare_rules_normalized ?? null,
          );
          // Tripjack `conditions.isBA` → form_conditions.hold_allowed (see /review)
          setHoldAllowed(!!fc?.hold_allowed);
          if (fc?.emergency_contact_required) setEmergencyContactRequired(true);
          if (fc?.pan_required) setPanRequired(true);
        } else {
          setTripjackBookingIdFromReview(null);
          setEmbeddedFareRulesFromReview(null);
          setReviewError('Could not load airline requirements. Please retry.');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setTripjackBookingIdFromReview(null);
        setEmbeddedFareRulesFromReview(null);
        console.warn('[review-on-mount] failed:', err?.message);
        const detail = err?.response?.data?.detail;
        // Sprint Feb-2026 · Backend now returns a structured payload for
        // mapped Tripjack errors (errCode 1000 = fare expired, 1009 etc).
        // If the action says "rebook", treat this as a hard fare-expiry
        // and surface the strong red banner with a "Search New Flights"
        // CTA — *not* a soft amber "Heads up" notice.
        if (detail && typeof detail === 'object') {
          if (detail.action === 'rebook' || detail.errCode === 1000 || detail.errCode === 1009) {
            setFareExpired(true);
            setReviewError(detail.user_message || 'This fare is no longer available. Please search again.');
          } else {
            setReviewError(detail.user_message || 'Could not load airline requirements. Please retry.');
          }
        } else {
          setReviewError(
            detail
            || 'Could not load airline requirements. You may still proceed; we will re-check at booking.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setReviewLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightData, bookingType, priceId, onwardPriceId, returnPriceId, reviewRefetchNonce]);

  // Initialize passengers array based on URL params
  useEffect(() => {
    const isInternational = flightData ? checkIsInternational(flightData) : false;
    const passengersArray = [];
    
    // Add adults
    for (let i = 0; i < numAdults; i++) {
      passengersArray.push({
        type: 'ADULT',
        title: 'Mr',
        firstName: '',
        lastName: '',
        gender: 'M',
        dob: '',
        passportNo: '',
        passportExpiry: '',
        passportIssueDate: '',  // Passport issue date
        nationality: 'IN',
        pan: '',  // T0 · PAN (required when conditions.ipa=true, v2.0.2)
        requiresPassport: isInternational
      });
    }
    
    // Add children
    for (let i = 0; i < numChildren; i++) {
      passengersArray.push({
        type: 'CHILD',
        title: 'Master',
        firstName: '',
        lastName: '',
        gender: 'M',
        dob: '',
        passportNo: '',
        passportExpiry: '',
        passportIssueDate: '',  // Passport issue date
        nationality: 'IN',
        pan: '',
        requiresPassport: isInternational
      });
    }
    
    // Add infants
    for (let i = 0; i < numInfants; i++) {
      passengersArray.push({
        type: 'INFANT',
        title: 'Master',
        firstName: '',
        lastName: '',
        gender: 'M',
        dob: '',
        passportNo: '',
        passportExpiry: '',
        passportIssueDate: '',  // Passport issue date
        nationality: 'IN',
        pan: '',
        requiresPassport: isInternational
      });
    }
    
    setPassengers(passengersArray);
  }, [numAdults, numChildren, numInfants, flightData]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.searchable-dropdown')) {
        setShowDropdown(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  
  // Countdown timer for fare expiry (30 minutes)
  useEffect(() => {
    if (!searchTimestamp) return;
    
    const FARE_VALIDITY_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    const timer = setInterval(() => {
      const now = Date.now();
      const elapsed = now - searchTimestamp;
      const remaining = FARE_VALIDITY_MS - elapsed;
      
      if (remaining <= 0) {
        setTimeRemaining(0);
        setFareExpired(true);
        clearInterval(timer);
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000); // Update every second
    
    return () => clearInterval(timer);
  }, [searchTimestamp]);
  
  // Load saved passengers when user or agent is authenticated
  useEffect(() => {
    if (isAuthenticated || isAgentAuthenticated) {
      loadSavedPassengers();
      // Auto-fill contact phone and email (B2C user or agent), same priority as phone
      if (user?.phone) {
        setContactPhone(user.phone);
      } else if (agent?.phone) {
        setContactPhone(agent.phone);
      }
      if (user?.email) {
        setContactEmail(String(user.email).trim());
      } else if (agent?.email) {
        setContactEmail(String(agent.email).trim());
      }
    }
  }, [isAuthenticated, isAgentAuthenticated, user, agent]);
  
  const loadSavedPassengers = async () => {
    try {
      let passengers = [];
      
      if (isAuthenticated) {
        // Load B2C user's saved passengers
        passengers = await getSavedPassengers();
      } else if (isAgentAuthenticated) {
        // Load agent's saved travellers
        const response = await axios.get(`${API}/agent/dashboard/travellers`, {
          withCredentials: true
        });
        passengers = response.data.travellers || [];
      }
      
      setSavedPassengers(passengers || []);
    } catch (error) {
      console.error('Failed to load saved passengers:', error);
      setSavedPassengers([]); // Set empty array on error to prevent UI issues
    }
  };
  
  // Handle autofill passenger from saved data
  const handleSelectSavedPassenger = (passengerIndex, savedPassengerId) => {
    if (!savedPassengerId) return;
    
    const savedPassenger = savedPassengers.find(p => p.id === savedPassengerId);
    if (!savedPassenger) return;
    
    // Update selected traveller IDs
    const updatedSelectedIds = [...selectedTravellerIds];
    updatedSelectedIds[passengerIndex] = savedPassengerId;
    setSelectedTravellerIds(updatedSelectedIds);
    
    const expiryRaw =
      savedPassenger.passport_expiry
      ?? savedPassenger.passportExpiry
      ?? savedPassenger.eD;
    const issueRaw =
      savedPassenger.passport_issue_date
      ?? savedPassenger.passportIssueDate
      ?? savedPassenger.pid;
    const dobRaw = savedPassenger.dob ?? savedPassenger.date_of_birth;

    const updatedPassengers = [...passengers];
    updatedPassengers[passengerIndex] = {
      ...updatedPassengers[passengerIndex],
      title: savedPassenger.title || updatedPassengers[passengerIndex].title,
      firstName: savedPassenger.first_name || '',
      lastName: savedPassenger.last_name || '',
      gender: savedPassenger.gender || updatedPassengers[passengerIndex].gender,
      dob: toHTMLDateInputValue(dobRaw) || '',
      passportNo: savedPassenger.passport_no || savedPassenger.passport_number || '',
      passportExpiry: toHTMLDateInputValue(expiryRaw) || '',
      passportIssueDate: toHTMLDateInputValue(issueRaw) || '',
      nationality: savedPassenger.nationality || savedPassenger.passport_nationality || 'IN'
    };
    setPassengers(updatedPassengers);
  };
  
  // Helper function to calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  // Helper function to get passenger type from DOB
  const getPassengerTypeFromDOB = (dob) => {
    const age = calculateAge(dob);
    if (age === null) return null;
    if (age >= 12) return 'ADULT';
    if (age >= 2) return 'CHILD';
    return 'INFANT';
  };
  
  // Filter travellers based on passenger type
  const getFilteredTravellers = (passengerIndex) => {
    const passenger = passengers[passengerIndex];
    const requiredType = passenger.type; // ADULT, CHILD, or INFANT
    
    return savedPassengers.filter(traveller => {
      // Skip already selected travellers (except current passenger's selection)
      if (selectedTravellerIds.includes(traveller.id) && 
          selectedTravellerIds[passengerIndex] !== traveller.id) {
        return false;
      }
      
      // If traveller has DOB, validate age matches passenger type
      if (traveller.dob) {
        const travellerType = getPassengerTypeFromDOB(traveller.dob);
        return travellerType === requiredType;
      }
      
      // If no DOB, allow for adults only (safer assumption)
      return requiredType === 'ADULT';
    });
  };
  
  const handleBooking = async () => {
    setError(null);
    
    // Validate contact details
    if (!contactEmail || !contactPhone) {
      setError('Please provide contact email and phone number');
      return;
    }
    
    if (!validateEmail(contactEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (!validatePhone(contactPhone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    // Validate GST if enabled OR mandatory by fare (Sprint-3 · SSOT §5.1)
    if (gstEnabled || formConditions?.gst_mandatory) {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      
      if (!gstDetails.gstNumber || gstDetails.gstNumber.length !== 15) {
        setError('GST number must be exactly 15 characters');
        return;
      }
      
      if (!gstRegex.test(gstDetails.gstNumber)) {
        setError('Invalid GST format. Example: 29ABCDE1234F1Z5');
        return;
      }
      
      if (!gstDetails.registeredName?.trim()) {
        setError('Company name is required for GST invoice');
        return;
      }
      
      if (!gstDetails.email?.trim() || !/\S+@\S+\.\S+/.test(gstDetails.email)) {
        setError('Valid GST email is required');
        return;
      }
      
      if (!gstDetails.mobile?.trim() || !/^\+?\d{10,15}$/.test(gstDetails.mobile)) {
        setError('Valid mobile with country code is required');
        return;
      }
      
      if (!gstDetails.address?.trim()) {
        setError('Registered address is required for GST invoice');
        return;
      }
    }
    
    // Prepare common variables outside try block for catch block access
    let priceIds = [];
    if (bookingType === 'roundtrip') {
      priceIds = [onwardPriceId, returnPriceId];
    } else if (bookingType === 'multicity') {
      // Sprint-2 · multi-city legs come from the synthesized flightData
      priceIds = (flightData?.priceIds || []).filter(Boolean);
    } else {
      priceIds = [priceId];
    }

    const supplierSource = resolveBookingPageSupplierSource(flightData, bookingType);
    
    const formattedPhone = contactPhone.startsWith('+') ? contactPhone : `+91${contactPhone}`;
    
    let totalAmount = 0;
    if (bookingType === 'oneway') {
      totalAmount = computeFlightTotal(flightData);
    } else if (bookingType === 'roundtrip') {
      // Round trip: prefer summed onward+return per-pax, fall back to legacy totalPrice
      const onwardTotal = computeFlightTotal(flightData?.onward);
      const returnTotal = computeFlightTotal(flightData?.return);
      const computed = onwardTotal + returnTotal;
      const legacy = Number(flightData?.totalPrice ?? 0) || 0;
      totalAmount = computed > 0 ? computed : legacy;
    } else if (bookingType === 'multicity') {
      // Sprint-2 · sum each leg's per-pax fare, fall back to synthesized total
      const legsTotal = (flightData?.legs || []).reduce(
        (sum, x) => sum + computeFlightTotal(x.flight),
        0
      );
      const legacy = Number(flightData?.totalPrice ?? 0) || 0;
      totalAmount = legsTotal > 0 ? legsTotal : legacy;
    } else {
      totalAmount = computeFlightTotal(flightData?.flight);
    }

    // Deeplink path: markup quote when search didn't bake service charge into fares.
    if (_quotedServiceCharge > 0) {
      totalAmount = Math.round((totalAmount + _quotedServiceCharge) * 100) / 100;
    }
    
    // Validate all passengers
    const isInternational = flightData ? checkIsInternational(flightData) : false;
    
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      const passengerLabel = `${p.type} ${i + 1}`;
      
      // Name validation
      if (!p.firstName || !validateName(p.firstName)) {
        setError(`${passengerLabel}: Please enter a valid first name (alphabets only, min 2 chars)`);
        return;
      }
      
      if (!p.lastName || !validateName(p.lastName)) {
        setError(`${passengerLabel}: Please enter a valid last name (alphabets only, min 2 chars)`);
        return;
      }
      
      // DOB mandatory for children, infants, and international flights
      if ((p.type === 'CHILD' || p.type === 'INFANT' || isInternational) && !p.dob) {
        setError(`${passengerLabel}: Date of birth is mandatory`);
        return;
      }
      
      // DOB validation if provided
      if (p.dob && !validateDOB(p.dob, p.type)) {
        setError(`${passengerLabel}: Invalid date of birth for ${p.type.toLowerCase()}`);
        return;
      }
      
      // Passport validation for international flights
      if (isInternational) {
        if (!p.passportNo || !validatePassport(p.passportNo)) {
          setError(`${passengerLabel}: Valid passport number required for international flights (6-15 alphanumeric)`);
          return;
        }
        
        if (!p.passportExpiry) {
          setError(`${passengerLabel}: Passport expiry date is required for international flights`);
          return;
        }
        
        // Get travel date from flight data
        const travelDate = flightData?.route?.departureDate || flightData?.flight?.departureTime || new Date().toISOString();
        if (!validatePassportExpiry(p.passportExpiry, travelDate)) {
          setError(`${passengerLabel}: Passport must be valid for at least 6 months from travel date`);
          return;
        }
      }
    }
    
    // All validations passed - prepare data
    
    setLoading(true);
    setError(null);
    setRepriceContinueHint('');
    
    try {
      // Prepare traveller info
      const travellerInfo = passengers.map(p => {
        const traveller = {
          ti: p.title,
          pt: p.type,
          fN: p.firstName.trim(),
          lN: p.lastName.trim()
        };
        
        // Send DOB whenever the user has filled it in. Tripjack v2.0 PDF
        // line 1339 accepts `dob` on adult travellers too (mandatory when
        // /review.conditions.pcs.dobe=true or .dob.adobr=true). Always
        // forwarding the user-supplied DOB is safe — Tripjack ignores it
        // when not needed and validates when it is.
        if (p.dob) {
          traveller.dob = p.dob;
        }
        
        // Add passport for international
        if (isInternational && p.passportNo && p.passportExpiry) {
          traveller.pNum = p.passportNo.toUpperCase();
          traveller.eD = p.passportExpiry;
          traveller.pNat = p.nationality;
          if (p.passportIssueDate) {
            traveller.pid = p.passportIssueDate;
          }
        }

        // T0 · PAN (Tripjack v2.0.2). Adults only — Tripjack does not collect
        // PAN for child/infant. Always pass through if user provided one so
        // the GST/SME flow keeps working too.
        if (p.type === 'ADULT' && p.pan) {
          traveller.pan = p.pan.trim().toUpperCase();
        }
        
        return traveller;
      });
      
      // For offline flights, skip Tripjack Review API and go directly to review
      if (supplierSource === 'offline') {
        console.log('🎫 Offline flight detected - skipping Tripjack Review API');
        
        const bookingDataForReview = {
          bookingType,
          priceId,
          priceIds,
          onwardPriceId,
          returnPriceId,
          passengers: travellerInfo,
          passengersRaw: passengers,
          contactEmail,
          contactPhone: formattedPhone,
          flightData,
          totalAmount,
          timestamp: new Date().toISOString(),
          reviewData: null,
          gstEnabled,
          gstDetails: (gstEnabled || formConditions?.gst_mandatory) ? gstDetails : null,
          commissionPreview: resolveCommissionPreview(null, null, agent),
        };
        
        setLoading(false);
        navigate('/review-booking', {
          state: { 
            bookingData: {
              ...bookingDataForReview,
              ssrSelections: null,
              addOnsIds: null
            }
          }
        });
        return;
      }
      
      // For Riya flights, skip Tripjack Review API. Riya uses its own
      // /api/riya/price + /api/riya/seat-map flow (called at backend-book-time
      // from agent_booking._maybe_execute_riya_booking), so we skip the 5-second
      // Tripjack timeout. We DO route through /ancillary-services (same page
      // Tripjack uses) — that page detects the Riya source and fetches the
      // catalogue via /api/riya/price directly.
      if (supplierSource === 'riya') {
        console.log('🟢 Riya flight detected - routing through /ancillary-services');
        const bookingDataForReview = {
          bookingType,
          priceId,
          priceIds,
          onwardPriceId,
          returnPriceId,
          passengers: travellerInfo,
          passengersRaw: passengers,
          contactEmail,
          contactPhone: formattedPhone,
          flightData,
          totalAmount,
          timestamp: new Date().toISOString(),
          reviewData: null,
          ssrSelections: null,
          // Sprint-2 · Preserve return-leg SSR (Riya RT) — not hardcoded
          returnSsrSelections: null,
          addOnsIds: null,
          gstEnabled,
          gstDetails: (gstEnabled || formConditions?.gst_mandatory) ? gstDetails : null,
          commissionPreview: resolveCommissionPreview(null, null, agent),
        };
        setLoading(false);
        navigate('/ancillary-services', {
          state: { bookingData: bookingDataForReview, reviewData: null },
        });
        return;
      }
      
      console.log('📞 Calling Tripjack Review to get SSR options...');
      
      // Add a timeout with fallback - if review takes >5 seconds, skip SSR
      const reviewAcceptance = pendingFareAcceptanceRef.current;
      const reviewPromise = axios.post(`${API}/tripjack/review`, {
        priceIds,
        ...(reviewAcceptance ? { acceptance: reviewAcceptance } : {}),
      }, { headers: { ...bookingSessionHeaders() } });

      // Sprint May-2026 · Bumped from 5s → 12s to accommodate the
      // backend's single-retry-on-supplier-flake path. Backend may now
      // take up to ~4s for retries on errCode 1000/1009/808; happy path
      // is still <2s. 12s gives headroom for slow networks too.
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ timeout: true }), 12000); // 12 second timeout
      });
      
      const result = await Promise.race([reviewPromise, timeoutPromise]);

      let reviewData = null;
      let formConditions = null;

      if (result.timeout) {
        // Sprint-2 · SSOT §6.3 — DO NOT proceed to payment when the
        // airline's per-fare requirements haven't loaded. Previously we
        // silently skipped SSR and let the user pay, which could 422 at
        // /book or charge them for an invalid fare. Hard-block here.
        console.warn('⚠️ Review API timeout — blocking payment per SSOT §6.3');
        setError(
          'We couldn\'t load the airline\'s booking requirements in time. ' +
          'Please check your connection and try again in a moment.'
        );
        setLoading(false);
        return;
      } else if (result.data?.success) {
        reviewData = result.data.data;
        formConditions = result.data.form_conditions || null;
        setCommissionPreview(result.data.commission_preview ?? null);
        console.log('✅ Got review data with SSR options', formConditions);

        // Cache form_conditions in component state so the render path
        // (e.g. adult-DOB field for fares with pcs.dobe=true) can react
        // to the requirements without rerunning this side effect.
        setFormConditions(formConditions);

        // Tripjack `conditions.isBA` → hold_allowed (only /review knows this)
        setHoldAllowed(!!formConditions?.hold_allowed);

        // B4 · Fare-alert warning (pre-payment). If Tripjack raised a
        // FAREALERT on this review, block here unless the user already accepted
        // the same old→new triple (supplier often echoes the alert on repeat /review).
        const fareAlert = result.data.fare_alert_summary;
        const fareAlreadyAccepted = result.data.fare_alert_already_accepted === true;
        const acceptanceMatches = fareAlert
          && fareAlertMatchesAcceptance(fareAlert, pendingFareAcceptanceRef.current);
        if (fareAlert && !fareAlreadyAccepted && !acceptanceMatches) {
          setFareRepriceDetail({
            code: 'FARE_CHANGED',
            action: 'REPRICE',
            fare_change_class: 'SOFT_REPRICE',
            message: fareAlert.message,
            old_fare: fareAlert.old_fare,
            new_fare: fareAlert.new_fare,
            delta: fareAlert.delta,
          });
          setFareRepricePriceIds(priceIds);
          setFareRepriceAttempt(0);
          setFareRepriceOpen(true);
          setLoading(false);
          return;
        }
        if (fareAlreadyAccepted || acceptanceMatches) {
          reviewData = result.data.data || cachedReviewData || reviewData;
          setCachedReviewData(reviewData);
          const bidFromReview = reviewData && (reviewData.bookingId || reviewData.orderId);
          if (bidFromReview) {
            setTripjackBookingIdFromReview(String(bidFromReview));
          }
        }
      } else {
        // Sprint-2 · SSOT §6.3 — /review failed. Block rather than sending
        // the user to payment without validated per-fare requirements.
        console.warn('⚠️ Review API failed — blocking payment per SSOT §6.3');
        setError(
          'Unable to load booking requirements from the airline. ' +
          'Please retry, or contact support if the issue persists.'
        );
        setLoading(false);
        return;
      }

      // ── Dynamic pax validation against Tripjack /review form_conditions ──
      // This is the pre-payment guard rail. Backend /book also validates (422
      // PAX_FIELDS_MISSING) as the authoritative check, but we catch it here
      // first so the user doesn't get taken to payment with incomplete info.
      if (formConditions && (formConditions.dob_required || formConditions.passport_required || formConditions.doc_id_required || formConditions.pan_required)) {
        const dobReq = formConditions.dob_required || {};
        const ppReq = formConditions.passport_required || {};
        const docReq = formConditions.doc_id_required || {};
        const panReq = !!formConditions.pan_required;
        const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
        const DOC_NUM_DI_RE = /^[A-Za-z0-9]+$/;
        const missing = [];
        passengers.forEach((p, idx) => {
          const key = (p.type || 'ADULT').toLowerCase();
          const paxLabel = `${p.type || 'ADULT'} ${idx + 1} (${p.firstName || ''} ${p.lastName || ''})`.trim();
          if (dobReq[key] && !p.dob) {
            missing.push(`${paxLabel}: Date of Birth is required for this fare`);
          }
          if (ppReq[key]) {
            if (!p.passportNo) missing.push(`${paxLabel}: Passport Number is required for this fare`);
            if (!p.passportExpiry) missing.push(`${paxLabel}: Passport Expiry is required for this fare`);
          }
          // G5 · Doc ID — passport satisfies this; ask for visa/national-id
          // only if neither passport nor docInfo is present.
          if (docReq[key] && !p.passportNo && !(p.docNumber && p.docType)) {
            missing.push(`${paxLabel}: A travel document (passport or national ID) is required for this fare`);
          }
          const docNumTrim = (p.docNumber || '').trim();
          if (docNumTrim && !DOC_NUM_DI_RE.test(docNumTrim)) {
            missing.push(
              `${paxLabel}: Document number must be letters and numbers only (no spaces or special characters)`
            );
          }
          // T0 · PAN required (adult only)
          if (panReq && p.type === 'ADULT') {
            const panVal = (p.pan || '').trim().toUpperCase();
            if (!panVal) {
              missing.push(`${paxLabel}: PAN is required for this fare`);
            } else if (!PAN_RE.test(panVal)) {
              missing.push(`${paxLabel}: Invalid PAN format (expected 10 chars, e.g. ABCDE1234F)`);
            }
          }
        });
        // If PAN is required, surface the field on the form so user can fill it
        if (panReq) setPanRequired(true);
        if (missing.length > 0) {
          setError(
            'This fare requires additional passenger information:\n• ' +
            missing.join('\n• ')
          );
          setLoading(false);
          return;
        }
      }

      // G3 · Emergency contact pre-flight check. Server is authoritative,
      // but we catch missing fields here to avoid taking a payment we can't
      // turn into a booking.
      if (formConditions?.emergency_contact_required) {
        setEmergencyContactRequired(true);
        if (!emergencyContact.name || !emergencyContact.email || !emergencyContact.phone) {
          setError(
            'This fare requires an emergency contact (name, email and phone). Please fill the Emergency Contact section below.'
          );
          setLoading(false);
          return;
        }
      }

      // Prepare booking data
      const bookingDataForReview = {
        bookingType,
        priceId,
        priceIds,
        onwardPriceId,
        returnPriceId,
        passengers: travellerInfo,
        passengersRaw: passengers,
        contactEmail,
        contactPhone: formattedPhone,
        flightData,
        totalAmount,
        timestamp: new Date().toISOString(),
        reviewData, // Include review data with SSR
        formConditions,  // Tripjack per-fare required-field rules
        // Feb-2026 · Thread Tripjack review `bookingId` downstream so
        // ReviewBooking's `fare-validate-instant` pre-flight can actually
        // run (was previously always undefined → silently skipped).
        tripjackBookingId: tripjackSessionBookingId(reviewData, tripjackBookingIdFromReview),
        embeddedFareRulesNormalized: result.data.embedded_fare_rules_normalized
          ?? embeddedFareRulesFromReview
          ?? null,
        ...(pendingFareAcceptanceRef.current
          ? { fareChangeAcceptance: pendingFareAcceptanceRef.current }
          : {}),
        gstEnabled,  // Add GST flag
        gstDetails: (gstEnabled || formConditions?.gst_mandatory) ? gstDetails : null,  // Add GST details
        emergencyContact: formConditions?.emergency_contact_required ? emergencyContact : null,  // G3
        bookingMode: holdAllowed && bookingMode === 'hold' ? 'hold' : 'instant',  // Sprint H1
        commissionPreview: result.data?.commission_preview ?? commissionPreview,
      };

      setLoading(false);

      // G.4 / Audit #14 — GDS (non-LCC): book-time SSR not supported; skip ancillaries.
      if (formConditions?.ssr_booking_allowed === false) {
        navigate('/review-booking', {
          state: {
            bookingData: {
              ...bookingDataForReview,
              ssrSelections: null,
              addOnsIds: null,
            },
          },
        });
        return;
      }

      // Navigate to Ancillary Services Page for Tripjack flights
      navigate('/ancillary-services', {
        state: {
          bookingData: bookingDataForReview,
          reviewData: reviewData,
          formConditions: formConditions,
        }
      });
      
    } catch (err) {
      console.error('Review error:', err);

      // Sprint-2 · SSOT §6.3 — Review exception = unknown per-fare rules.
      // Block for Tripjack flights; Riya/offline use their own catalogue so
      // they can still proceed. Previously we let TJ proceed silently,
      // which could 422 at /book or accept invalid pax data.
      const srcLower = supplierSource;
      const isTripjack = !srcLower || srcLower === 'tripjack';

      if (isTripjack) {
        console.warn('⚠️ Tripjack review threw — blocking payment per SSOT §6.3');
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === 'object') {
          // Structured Tripjack error (errCode 1000 / 1009 etc.)
          if (detail.action === 'rebook' || detail.errCode === 1000 || detail.errCode === 1009) {
            setFareExpired(true);
          }
          setError(detail.user_message || 'Unable to load booking requirements. Please retry.');
        } else {
          setError(
            detail
            || 'Unable to load booking requirements from the airline. '
            + 'Please retry, or contact support if the issue persists.'
          );
        }
        setLoading(false);
        return;
      }

      // Non-Tripjack fallback (offline / other): still allow the funnel.
      console.warn('⚠️ Proceeding without SSR options (non-Tripjack source)');
      
      // Prepare traveller info again for catch block
      const isInternational = flightData ? checkIsInternational(flightData) : false;
      const travellerInfo = passengers.map(p => {
        const traveller = {
          ti: p.title,
          pt: p.type,
          fN: p.firstName.trim(),
          lN: p.lastName.trim()
        };
        if (p.dob) {
          traveller.dob = p.dob;
        }
        if (isInternational && p.passportNo && p.passportExpiry) {
          traveller.pNum = p.passportNo.toUpperCase();
          traveller.eD = p.passportExpiry;
          traveller.pNat = p.nationality;
          if (p.passportIssueDate) {
            traveller.pid = p.passportIssueDate;
          }
        }
        // T0 · PAN pass-through (adult only)
        if (p.type === 'ADULT' && p.pan) {
          traveller.pan = p.pan.trim().toUpperCase();
        }
        return traveller;
      });
      
      const bookingDataForReview = {
        bookingType,
        priceId,
        priceIds,
        onwardPriceId,
        returnPriceId,
        passengers: travellerInfo,
        passengersRaw: passengers,
        contactEmail,
        contactPhone: formattedPhone,
        flightData,
        totalAmount,
        timestamp: new Date().toISOString()
      };
      
      // Navigate to Ancillary Services even on error (without SSR data).
      // Offline flights skip ancillary entirely. Riya flights now go to
      // /ancillary-services (which fetches the Riya catalogue itself).
      if (supplierSource === 'offline') {
        console.log(`🎫 ${supplierSource} flight - skipping ancillary`);
        navigate('/review-booking', {
          state: { 
            bookingData: {
              ...bookingDataForReview,
              ssrSelections: null,
              addOnsIds: null
            }
          }
        });
      } else {
        navigate('/ancillary-services', {
          state: { 
            bookingData: bookingDataForReview,
            reviewData: null
          }
        });
      }
      
      setLoading(false);
    }
  };
  
  const updatePassenger = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
  };
  
  // Error state - No flight data
  if (!flightData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">No Flight Selected</h2>
          <p className="text-gray-600 mb-8">{error || 'Please search and select a flight first.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-4 bg-[#1B375B] text-white rounded-xl font-bold hover:bg-[#335E77] transition-colors shadow-lg"
          >
            Search Flights
          </button>
        </div>
        <Footer />
      </div>
    );
  }
  
  // Success state - Booking confirmed
  if (bookingSuccess && bookingDetails) {
    const displayFlight = bookingType === 'oneway' ? flightData : 
                         bookingType === 'roundtrip' ? flightData.onward : 
                         flightData.flight;
    const totalPrice = bookingType === 'oneway' ? computeFlightTotal(flightData) : 
                      bookingType === 'roundtrip' ? (computeFlightTotal(flightData.onward) + computeFlightTotal(flightData.return)) || flightData.totalPrice : 
                      computeFlightTotal(flightData.flight);
    // Sprint-7 F3 · Resolve airport metadata from authoritative `airports.json`
    // (5,379 IATA codes) instead of the 14-entry `data/mock` list.
    const fromAirport = getAirportInfo(flightData.route?.from || displayFlight.from);
    const toAirport = getAirportInfo(flightData.route?.to || displayFlight.to);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-teal-500 px-8 py-10 text-center">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Check className="w-14 h-14 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-3">Booking Successful!</h1>
              <p className="text-green-100 text-lg">Your flight has been booked successfully</p>
            </div>
            
            {/* Booking Details */}
            <div className="p-4 sm:p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-6">
                  <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                    <Plane className="w-5 h-5 text-blue-600" />
                    Booking Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Booking ID</p>
                      <p className="font-mono font-bold text-sm text-blue-600">{getUserFacingBookingId(bookingDetails)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>
                      <p className="font-semibold text-green-600 flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {bookingDetails.status || 'HOLD'}
                      </p>
                    </div>
                    {bookingDetails.fullDetails?.travellers?.[0]?.pnr && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">PNR Number</p>
                        <p className="font-bold text-lg">{bookingDetails.fullDetails.travellers[0].pnr}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 sm:p-6">
                  <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-600" />
                    Passenger Details
                  </h3>
                  <div className="space-y-3">
                    {passengers.map((p, idx) => (
                      <div key={idx}>
                        <p className="text-xs text-gray-500 uppercase">{p.type} {idx + 1}</p>
                        <p className="font-semibold text-gray-800">{p.title} {p.firstName} {p.lastName}</p>
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500 uppercase">Contact</p>
                      <p className="text-sm text-gray-600">{contactEmail}</p>
                      <p className="text-sm text-gray-600">{contactPhone}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Flight Summary */}
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 text-white">
                <h3 className="font-bold text-lg mb-4">Flight Summary</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{fromAirport?.code}</p>
                    <p className="text-sm opacity-90">{fromAirport?.city}</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <div className="h-[2px] flex-1 bg-white/50 w-24"></div>
                      <Plane className="w-5 h-5 rotate-90" />
                      <div className="h-[2px] flex-1 bg-white/50 w-24"></div>
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{toAirport?.code}</p>
                    <p className="text-sm opacity-90">{toAirport?.city}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
                  <span className="text-sm opacity-90">Total Amount Paid</span>
                  <span className="text-3xl font-bold">₹{totalPrice.toLocaleString()}</span>
                </div>
              </div>
              
              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-amber-900 mb-2">Important Notice</h4>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      This is a <strong>HOLD booking</strong>. Please complete payment within <strong>24 hours</strong> to confirm your ticket. 
                      Booking details and payment link have been sent to <strong>{contactEmail}</strong>.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    localStorage.removeItem('selectedFlight');
                    navigate('/');
                  }}
                  className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Book Another Flight
                </button>
                <button
                  onClick={() => navigate('/bookings')}
                  className="flex-1 px-6 py-4 bg-[#1B375B] text-white rounded-xl font-semibold hover:bg-[#335E77] transition-colors shadow-lg"
                >
                  View My Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <BottomNav />
      </div>
    );
  }
  
  // Get flight details for display
  let displayFlight = null;
  let returnFlight = null;
  let totalPrice = 0;
  
  if (bookingType === 'oneway') {
    displayFlight = flightData;
    totalPrice = computeFlightTotal(flightData);
  } else if (bookingType === 'roundtrip') {
    displayFlight = flightData.onward;
    returnFlight = flightData.return;
    const computed = computeFlightTotal(flightData.onward) + computeFlightTotal(flightData.return);
    totalPrice = computed > 0 ? computed : (Number(flightData.totalPrice) || 0);
  } else if (bookingType === 'combo') {
    displayFlight = flightData.flight;
    totalPrice = computeFlightTotal(flightData.flight);
  }
  
  // Safety check: If displayFlight is still null, redirect back to search
  if (!displayFlight) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-3 sm:p-4">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Flight Selected</h2>
          <p className="text-gray-600 mb-6">Please select a flight from the search results to proceed with booking.</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Sprint-7 F3 · Authoritative airport lookup (5,379 entries vs 14 in mock).
  const fromAirport = getAirportInfo(flightData.route?.from || displayFlight?.from);
  const toAirport = getAirportInfo(flightData.route?.to || displayFlight?.to);

  // Sprint May-2026 · SSOT — Base / Taxes / Service Charge (incl. RT + MC legs).
  // See `utils/deriveDisplayFare.js`. Replaces duplicated inline math so
  // Ancillary + Review + Booking stay aligned.
  const fareDisplay = deriveDisplayFare({
    flightData,
    bookingType,
    numAdults,
    numChildren,
    numInfants,
    supplierTotalOverride: fareRepriceOverrideTotal,
  });
  const baseFare = fareDisplay.baseFare;
  const taxes = fareDisplay.taxes;
  const serviceCharge = fareDisplay.serviceCharge;
  const hasFareBreakdown = fareDisplay.hasFareBreakdown;
  const _paxLine = fareDisplay.paxLine;
  const _muSubLine = fareDisplay.muSubLine;

  // ── Effective service charge: prefer the search-response value, fall
  // back to the quote on deeplinks (the markup quote effect lives at the
  // top of the component to comply with React's rules-of-hooks).
  const _effectiveServiceCharge = serviceCharge > 0 ? serviceCharge : _quotedServiceCharge;

  const commissionFare = deriveCommissionFare({
    amountToPay: totalPrice,
    commissionPreview: resolveCommissionPreview(
      { commission_preview: commissionPreview },
      null,
      agent,
    ),
  });
  
  // Segment Card Component (matches reference image structure)
  const SegmentCard = ({ segment, segmentIndex, totalSegments, legFlight }) => {
    const lf = legFlight ?? displayFlight;
    const depTime = segment.dt?.split('T')[1]?.substring(0, 5) || segment.departureTime || 'N/A';
    const arrTime = segment.at?.split('T')[1]?.substring(0, 5) || segment.arrivalTime || 'N/A';
    const depDate = segment.dt?.split('T')[0] || '';
    const arrDate = segment.at?.split('T')[0] || '';
    
    const segDuration = segment.duration || 0;
    const segHours = Math.floor(segDuration / 60);
    const segMins = segDuration % 60;
    const durationStr = `${segHours}h ${segMins}m`;
    
    const airlineCode = segment.fD?.aI?.code || segment.airlineCode;
    const airlineName = segment.fD?.aI?.name || segment.airline;
    const flightNumber = segment.fD?.fN || segment.flightNumber;
    const aircraftType = segment.fD?.eT || segment.aircraftType;
    
    const depAirportCode = segment.da?.code || segment.from;
    const depAirportCity = segment.da?.city || segment.fromCity;
    const depAirportName = segment.da?.name || fromAirport?.name;
    const depTerminal = segment.da?.terminal;
    
    const arrAirportCode = segment.aa?.code || segment.to;
    const arrAirportCity = segment.aa?.city || segment.toCity;
    const arrAirportName = segment.aa?.name || toAirport?.name;
    const arrTerminal = segment.aa?.terminal;
    
    // Sprint-7 F2 · Drop hardcoded `'7 Kg'` / `'15 Kg'` defaults — surface
    // only what the supplier actually returned. Render rows hide when both
    // values are absent.
    const { checked: checkInBaggage, cabin: cabinBaggage } = resolveTripjackSegmentBaggage(segment, lf, lf);
    const hasMeal = segment.ssrInfo?.MEAL?.length > 0 || displayFlight?.mealIncluded || false;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-[#1B375B] to-[#335E77] px-3 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
              {airlineLogos[airlineCode]?.square || airlineLogos[airlineCode]?.best ? (
                <img 
                  src={airlineLogos[airlineCode].square || airlineLogos[airlineCode].best} 
                  alt={airlineName} 
                  className="w-full h-full object-contain" 
                />
              ) : (
                <Plane className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <p className="text-xs font-bold text-white leading-tight">{airlineName} • {flightNumber}</p>
              <p className="text-[9px] text-gray-300 leading-tight">{aircraftType || 'Aircraft'} • {durationStr}</p>
            </div>
          </div>
          {segmentIndex !== undefined && totalSegments > 1 && (
            <div className="bg-white/20 px-2 py-0.5 rounded-full">
              <p className="text-[9px] font-bold text-white">{segmentIndex + 1}/{totalSegments}</p>
            </div>
          )}
        </div>
        
        {/* Compact Flight Times */}
        <div className="p-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
            {/* Departure */}
            <div>
              <p className="text-[9px] text-gray-500 uppercase mb-0.5">Departure</p>
              <p className="text-2xl font-black text-[#1B375B] leading-none mb-0.5">{depTime}</p>
              <p className="text-xs font-bold text-gray-800 leading-tight">{depAirportCode}</p>
              <p className="text-[10px] text-gray-600 leading-tight">{depAirportCity}</p>
              {depTerminal && (
                <span className="inline-block mt-0.5 text-[8px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                  {depTerminal}
                </span>
              )}
            </div>
            
            {/* Center Line - Very Compact */}
            <div className="flex flex-col items-center justify-center px-1">
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-1.5 h-1.5 rounded-full border-2 border-[#1B375B]" />
                <div className="h-8 w-[2px] bg-gradient-to-b from-[#1B375B] to-[#659A9B]" />
                <Plane className="w-4 h-4 text-[#659A9B] rotate-90" />
                <div className="h-8 w-[2px] bg-gradient-to-b from-[#659A9B] to-[#1B375B]" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
              </div>
            </div>
            
            {/* Arrival */}
            <div className="text-right">
              <p className="text-[9px] text-gray-500 uppercase mb-0.5">Arrival</p>
              <p className="text-2xl font-black text-[#1B375B] leading-none mb-0.5">{arrTime}</p>
              <p className="text-xs font-bold text-gray-800 leading-tight">{arrAirportCode}</p>
              <p className="text-[10px] text-gray-600 leading-tight">{arrAirportCity}</p>
              {arrTerminal && (
                <span className="inline-block mt-0.5 text-[8px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                  {arrTerminal}
                </span>
              )}
            </div>
          </div>
          
          {/* Sprint-7 F2 · Compact Baggage & Meal — baggage block hides when
              supplier didn't surface real values; meal block always renders. */}
          <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-200">
            {(cabinBaggage || checkInBaggage) ? (
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded bg-[#659A9B]/10 flex items-center justify-center flex-shrink-0">
                  <Luggage className="w-3.5 h-3.5 text-[#659A9B]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[8px] text-gray-500 uppercase font-semibold leading-tight">Baggage</p>
                  {cabinBaggage && (
                    <p className="text-[10px] font-bold text-gray-800 truncate leading-tight">Cabin: {cabinBaggage}</p>
                  )}
                  {checkInBaggage && (
                    <p className="text-[10px] font-bold text-gray-800 truncate leading-tight">Check-in: {checkInBaggage}</p>
                  )}
                </div>
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Utensils className="w-3.5 h-3.5 text-gray-600" />
              </div>
              <div>
                <p className="text-[8px] text-gray-500 uppercase font-semibold leading-tight">Meal</p>
                <p className={`text-[10px] font-bold leading-tight ${hasMeal ? 'text-green-600' : 'text-gray-500'}`}>
                  {hasMeal ? 'Included' : 'Not Included'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const bookingSourceLower = resolveBookingPageSupplierSource(flightData, bookingType);
  const isTripjackPaxGate =
    bookingSourceLower === 'tripjack' && bookingType !== 'multicity';
  const passengersUnlocked =
    !isTripjackPaxGate
    || (!!tripjackBookingIdFromReview && !reviewLoading && !fareExpired);
  const tripjackSkipAddons =
    isTripjackPaxGate
    && formConditions
    && formConditions.ssr_booking_allowed === false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button & Auth Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Search Results
          </button>
          
          {/* Login/User Info */}
          {isAgentAuthenticated && agent ? (
            // Agent logged in
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Agent</p>
                  <p className="text-sm font-bold text-gray-800">{agent.full_name}</p>
                </div>
              </div>
              <AgentBalanceBadge agent={agent} variant="compact" />
            </div>
          ) : isAuthenticated && user ? (
            // B2C User logged in
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1B375B] to-[#659A9B] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{user.full_name}</p>
                  <p className="text-xs text-gray-500">+91 {user.phone}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
              >
                <LogOut className="w-3 h-3" />
                Logout
              </button>
            </div>
          ) : (
            // Not logged in
            <button
              onClick={() => setShowLoginModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <LogIn className="w-4 h-4" />
              Login to Save Passengers
            </button>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Flight Segments & Passenger Form */}
          <div className="flex-1 space-y-6">
            {/* Flight Segments - Direct Display */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-4 py-3 md:px-6 md:py-4">
                <h2 className="text-white font-bold text-base md:text-lg">Flight Details</h2>
              </div>
              
              <div className="p-3 md:p-6 space-y-3 md:space-y-4">
                {/* ONE-WAY FLIGHTS - Direct segments */}
                {bookingType === 'oneway' && flightData?.segments && flightData.segments.length > 0 && (
                  flightData.segments.map((segment, idx) => (
                    <div key={idx}>
                      <SegmentCard 
                        segment={segment} 
                        segmentIndex={idx}
                        totalSegments={flightData.segments.length}
                        legFlight={flightData}
                      />
                      
                      {/* Layover between segments */}
                      {idx < flightData.segments.length - 1 && segment.cT && (
                        <div className="flex items-center justify-center gap-2 py-2 md:py-4">
                          <div className="flex items-center gap-2 md:gap-3 bg-orange-50 border-2 border-orange-300 px-3 py-1.5 md:px-5 md:py-3 rounded-full shadow-sm">
                            <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0" />
                            <p className="text-xs md:text-sm text-orange-800 font-bold">
                              <span className="hidden md:inline">Change of Planes • </span>
                              {Math.floor(segment.cT / 60)}h {segment.cT % 60}m<span className="hidden md:inline"> layover</span> in {segment.aa?.city}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}

                {/* DOMESTIC ROUND-TRIP - Separate onward and return */}
                {bookingType === 'roundtrip' && (
                  <>
                    {/* Onward Journey Header - Clean Design */}
                    <div className="border-l-4 border-[#659A9B] bg-[#659A9B]/5 px-4 py-3 rounded-r-xl">
                      <p className="text-[#1B375B] font-bold text-sm md:text-base flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#659A9B] flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-white" />
                        </div>
                        Onward Journey
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm mt-1.5 ml-10">
                        {flightData.route?.from} → {flightData.route?.to} • {flightData.route?.departureDate && new Date(flightData.route.departureDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Onward Segments */}
                    {flightData.onward?.segments ? (
                      flightData.onward.segments.map((segment, idx) => (
                        <div key={`onward-${idx}`}>
                          <SegmentCard 
                            segment={segment} 
                            segmentIndex={idx}
                            totalSegments={flightData.onward.segments.length}
                            legFlight={flightData.onward}
                          />
                          {idx < flightData.onward.segments.length - 1 && segment.cT && (
                            <div className="flex items-center justify-center gap-2 py-2 md:py-4">
                              <div className="flex items-center gap-2 md:gap-3 bg-orange-50 border-2 border-orange-300 px-3 py-1.5 md:px-5 md:py-3 rounded-full shadow-sm">
                                <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0" />
                                <p className="text-xs md:text-sm text-orange-800 font-bold">
                                  {Math.floor(segment.cT / 60)}h {segment.cT % 60}m layover in {segment.aa?.city}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <SegmentCard segment={flightData.onward} segmentIndex={0} totalSegments={1} legFlight={flightData.onward} />
                    )}

                    {/* Return Journey Header - Clean Design */}
                    <div className="border-l-4 border-[#1B375B] bg-[#1B375B]/5 px-4 py-3 rounded-r-xl mt-6">
                      <p className="text-[#1B375B] font-bold text-sm md:text-base flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#1B375B] flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-white rotate-180" />
                        </div>
                        Return Journey
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm mt-1.5 ml-10">
                        {flightData.route?.to} → {flightData.route?.from} • {flightData.route?.returnDate && new Date(flightData.route.returnDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Return Segments */}
                    {flightData.return?.segments ? (
                      flightData.return.segments.map((segment, idx) => (
                        <div key={`return-${idx}`}>
                          <SegmentCard 
                            segment={segment} 
                            segmentIndex={idx}
                            totalSegments={flightData.return.segments.length}
                            legFlight={flightData.return}
                          />
                          {idx < flightData.return.segments.length - 1 && segment.cT && (
                            <div className="flex items-center justify-center gap-2 py-2 md:py-4">
                              <div className="flex items-center gap-2 md:gap-3 bg-orange-50 border-2 border-orange-300 px-3 py-1.5 md:px-5 md:py-3 rounded-full shadow-sm">
                                <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0" />
                                <p className="text-xs md:text-sm text-orange-800 font-bold">
                                  {Math.floor(segment.cT / 60)}h {segment.cT % 60}m layover in {segment.aa?.city}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <SegmentCard segment={flightData.return} segmentIndex={0} totalSegments={1} legFlight={flightData.return} />
                    )}
                  </>
                )}

                {/* INTERNATIONAL COMBO - onwardSegments and returnSegments */}
                {bookingType === 'combo' && flightData.flight && (
                  <>
                    {/* Onward Journey Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-3 md:px-4 py-2 rounded-lg">
                      <p className="text-white font-bold text-sm md:text-base flex items-center gap-2">
                        <ArrowRight className="w-4 h-4" />
                        Onward: {flightData.route?.from} → {flightData.route?.to}
                      </p>
                      <p className="text-blue-100 text-xs md:text-sm mt-0.5">
                        {flightData.route?.departureDate && new Date(flightData.route.departureDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Onward Segments */}
                    {flightData.flight.onwardSegments && flightData.flight.onwardSegments.map((segment, idx) => (
                      <div key={`onward-${idx}`}>
                        <SegmentCard 
                          segment={segment} 
                          segmentIndex={idx}
                          totalSegments={flightData.flight.onwardSegments.length}
                          legFlight={flightData.flight || flightData}
                        />
                        {idx < flightData.flight.onwardSegments.length - 1 && segment.cT && (
                          <div className="flex items-center justify-center gap-2 py-2 md:py-4">
                            <div className="flex items-center gap-2 md:gap-3 bg-orange-50 border-2 border-orange-300 px-3 py-1.5 md:px-5 md:py-3 rounded-full shadow-sm">
                              <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0" />
                              <p className="text-xs md:text-sm text-orange-800 font-bold">
                                {Math.floor(segment.cT / 60)}h {segment.cT % 60}m layover in {segment.aa?.city}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Return Journey Header */}
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-3 md:px-4 py-2 rounded-lg mt-4">
                      <p className="text-white font-bold text-sm md:text-base flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        Return: {flightData.route?.to} → {flightData.route?.from}
                      </p>
                      <p className="text-purple-100 text-xs md:text-sm mt-0.5">
                        {flightData.route?.returnDate && new Date(flightData.route.returnDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Return Segments */}
                    {flightData.flight.returnSegments && flightData.flight.returnSegments.map((segment, idx) => (
                      <div key={`return-${idx}`}>
                        <SegmentCard 
                          segment={segment} 
                          segmentIndex={idx}
                          totalSegments={flightData.flight.returnSegments.length}
                          legFlight={flightData.flight || flightData}
                        />
                        {idx < flightData.flight.returnSegments.length - 1 && segment.cT && (
                          <div className="flex items-center justify-center gap-2 py-2 md:py-4">
                            <div className="flex items-center gap-2 md:gap-3 bg-orange-50 border-2 border-orange-300 px-3 py-1.5 md:px-5 md:py-3 rounded-full shadow-sm">
                              <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600 flex-shrink-0" />
                              <p className="text-xs md:text-sm text-orange-800 font-bold">
                                {Math.floor(segment.cT / 60)}h {segment.cT % 60}m layover in {segment.aa?.city}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {/* Fallback - if no proper structure found */}
                {!flightData?.segments && bookingType === 'oneway' && (
                  <SegmentCard 
                    segment={{
                      fD: {
                        aI: {
                          code: displayFlight.airlineCode,
                          name: displayFlight.airline
                        },
                        fN: displayFlight.flightNumber,
                        eT: displayFlight.aircraftType
                      },
                      departureTime: displayFlight.departureTime,
                      arrivalTime: displayFlight.arrivalTime,
                      from: displayFlight.from,
                      to: displayFlight.to,
                      fromCity: displayFlight.fromCity,
                      toCity: displayFlight.toCity,
                      duration: displayFlight.duration,
                      airlineCode: displayFlight.airlineCode,
                      airline: displayFlight.airline
                    }} 
                    segmentIndex={0}
                    totalSegments={1}
                    legFlight={displayFlight}
                  />
                )}
                
                {/* Seats Left Warning */}
                {displayFlight.seatsLeft && displayFlight.seatsLeft <= 9 && (
                  <div className="mt-4 bg-orange-50 border-2 border-orange-200 rounded-xl p-4 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                    <p className="text-sm text-orange-800">
                      <span className="font-bold">Only {displayFlight.seatsLeft} seats left</span> at this price!
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Tripjack: detailed fare rules (same FareRulesPanel + REVIEW + bookingId as ReviewBooking). */}
            {isTripjackPaxGate && (
              tripjackBookingIdFromReview ? (
                <FareRulesPanel
                  priceId={bookingType === 'roundtrip' ? (onwardPriceId || priceId) : priceId}
                  priceIds={bookingType === 'roundtrip' ? [onwardPriceId, returnPriceId].filter(Boolean) : undefined}
                  bookingId={tripjackBookingIdFromReview}
                  flowType="REVIEW"
                  embeddedNormalizedFareRules={embeddedFareRulesFromReview}
                />
              ) : (
                <div
                  className="bg-white rounded-2xl shadow-sm border border-amber-100 p-4"
                  data-testid="fare-rules-pending-card"
                >
                  <p className="text-sm font-bold text-[#1B375B]">Cancellation &amp; change policy</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {reviewLoading
                      ? 'Confirming this fare with the airline. Detailed rules will appear here in a moment.'
                      : 'We need a successful fare check before passenger details. Retry below or go back to search results.'}
                  </p>
                  {reviewError && !reviewLoading && (
                    <button
                      type="button"
                      className="mt-3 px-4 py-2 text-xs font-semibold rounded-lg bg-[#1B375B] text-white hover:bg-[#335E77]"
                      onClick={() => setReviewRefetchNonce((n) => n + 1)}
                    >
                      Retry fare check
                    </button>
                  )}
                </div>
              )
            )}

            {isTripjackPaxGate &&
              Array.isArray(formConditions?.messages) &&
              formConditions.messages.length > 0 && (
              <div
                className="bg-sky-50 border border-sky-200 rounded-2xl p-4 shadow-sm"
                data-testid="tripjack-supplier-messages"
              >
                <p className="text-xs font-bold text-[#1B375B] uppercase tracking-wide mb-2">
                  Airline / supplier notices
                </p>
                <ul className="space-y-2 list-none pl-0 m-0">
                  {formConditions.messages.map((m, idx) => {
                    const text = m && m.message != null ? String(m.message) : '';
                    const typ = m && m.type != null ? String(m.type) : '';
                    if (!text && !typ) return null;
                    return (
                      <li key={idx} className="text-sm text-gray-800 whitespace-pre-line">
                        {typ ? (
                          <span className="font-semibold text-gray-600 mr-2">{typ}:</span>
                        ) : null}
                        {text}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="relative space-y-6" data-testid="booking-pax-gate-wrap">
              {isTripjackPaxGate && !passengersUnlocked && (
                <div className="absolute inset-0 z-[5] flex items-start justify-center pt-6 sm:pt-10 px-3 bg-white/75 backdrop-blur-[1px] rounded-2xl border border-gray-100">
                  <div className="max-w-md w-full bg-white shadow-lg rounded-xl border border-[#1B375B]/15 p-4 text-center">
                    {reviewLoading && (
                      <Loader className="w-7 h-7 text-[#659A9B] animate-spin mx-auto" />
                    )}
                    <p className={`text-sm font-semibold text-[#1B375B] ${reviewLoading ? 'mt-3' : ''}`}>
                      {reviewLoading ? 'Confirming fare with airline…' : 'Passenger details unlock after fare is confirmed'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Review flight details and cancellation policy above while we verify this price.
                    </p>
                    {reviewError && !reviewLoading && (
                      <button
                        type="button"
                        className="mt-3 px-4 py-2 text-xs font-semibold rounded-lg bg-[#1B375B] text-white hover:bg-[#335E77]"
                        onClick={() => setReviewRefetchNonce((n) => n + 1)}
                      >
                        Retry fare check
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div
                className={
                  isTripjackPaxGate && !passengersUnlocked
                    ? 'space-y-6 pointer-events-none opacity-[0.55] select-none'
                    : 'space-y-6'
                }
              >
            {/* Passenger Forms - Dynamic */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-[#1B375B] mb-6">Passenger Details</h3>
              
              <div className="space-y-6">
                {passengers.map((passenger, index) => {
                  // Calculate correct passenger number per type
                  let typeIndex = 1;
                  if (passenger.type === 'ADULT') {
                    typeIndex = index + 1;
                  } else if (passenger.type === 'CHILD') {
                    typeIndex = index - numAdults + 1;
                  } else if (passenger.type === 'INFANT') {
                    typeIndex = index - numAdults - numChildren + 1;
                  }
                  
                  const passengerTypeLabel = `${passenger.type} ${typeIndex}`;
                  const titleOptions = getTitleOptions(passenger.type);
                  const isInternational = flightData ? checkIsInternational(flightData) : false;
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-[#1B375B] flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {passengerTypeLabel}
                        </h4>
                        
                        {/* Saved Passenger Selector (for logged-in users or agents) - Searchable */}
                        {(isAuthenticated || isAgentAuthenticated) && savedPassengers.length > 0 && (
                          <div className="relative searchable-dropdown">
                            <button
                              type="button"
                              onClick={() => {
                                setActivePassengerIndex(index);
                                setShowDropdown(!showDropdown || activePassengerIndex !== index);
                                setSearchTerm('');
                              }}
                              className="text-sm px-3 py-1.5 border border-[#659A9B] rounded-lg bg-white text-[#1B375B] font-semibold hover:bg-[#659A9B]/5 transition-colors flex items-center gap-2"
                            >
                              <User className="w-4 h-4" />
                              {selectedTravellerIds[index] ? 'Change' : 'Use Saved'} {isAgentAuthenticated ? 'Traveller' : 'Passenger'}
                            </button>
                            
                            {showDropdown && activePassengerIndex === index && (
                              <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 max-h-96 flex flex-col">
                                {/* Header with passenger type info */}
                                <div className="px-3 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-t-xl">
                                  <p className="text-xs font-semibold">
                                    Select {passenger.type} {isAgentAuthenticated ? 'Traveller' : 'Passenger'}
                                    {passenger.type === 'ADULT' && ' (Age 12+)'}
                                    {passenger.type === 'CHILD' && ' (Age 2-11)'}
                                    {passenger.type === 'INFANT' && ' (Age 0-1)'}
                                  </p>
                                </div>
                                
                                {/* Search input */}
                                <div className="p-3 border-b border-gray-200">
                                  <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name or passport..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                                    autoFocus
                                  />
                                </div>
                                
                                {/* Results list */}
                                <div className="overflow-y-auto flex-1">
                                  {(() => {
                                    const filteredTravellers = getFilteredTravellers(index);
                                    const searchFiltered = filteredTravellers.filter(sp => {
                                      if (!searchTerm) return true;
                                      const search = searchTerm.toLowerCase();
                                      const fullName = `${sp.first_name} ${sp.last_name}`.toLowerCase();
                                      const passport = (sp.passport_no || sp.passport_number || '').toLowerCase();
                                      return fullName.includes(search) || passport.includes(search);
                                    });
                                    
                                    if (searchFiltered.length === 0) {
                                      return (
                                        <div className="p-4 text-center">
                                          <p className="text-gray-500 text-sm mb-2">
                                            {filteredTravellers.length === 0 
                                              ? `No ${passenger.type.toLowerCase()} ${isAgentAuthenticated ? 'travellers' : 'passengers'} available`
                                              : 'No matching results'}
                                          </p>
                                          {filteredTravellers.length === 0 && (
                                            <p className="text-xs text-gray-400">
                                              {passenger.type === 'ADULT' && 'Saved travellers must be 12+ years old'}
                                              {passenger.type === 'CHILD' && 'Saved travellers must be 2-11 years old'}
                                              {passenger.type === 'INFANT' && 'Saved travellers must be under 2 years old'}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    
                                    return searchFiltered.map((sp) => {
                                      const age = sp.dob ? calculateAge(sp.dob) : null;
                                      const isSelected = selectedTravellerIds[index] === sp.id;
                                      
                                      return (
                                        <button
                                          key={sp.id}
                                          type="button"
                                          onClick={() => {
                                            handleSelectSavedPassenger(index, sp.id);
                                            setShowDropdown(false);
                                            setSearchTerm('');
                                          }}
                                          className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-0 ${
                                            isSelected 
                                              ? 'bg-[#659A9B]/20 hover:bg-[#659A9B]/30' 
                                              : 'hover:bg-[#659A9B]/10'
                                          }`}
                                        >
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <p className="font-semibold text-gray-800 flex items-center gap-2">
                                                {sp.title} {sp.first_name} {sp.last_name}
                                                {isSelected && (
                                                  <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                                                    Selected
                                                  </span>
                                                )}
                                              </p>
                                              {age !== null && (
                                                <p className="text-xs text-gray-600 mt-1">
                                                  Age: {age} years
                                                </p>
                                              )}
                                              {(sp.passport_no || sp.passport_number) && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                  Passport: {sp.passport_no || sp.passport_number}
                                                </p>
                                              )}
                                              {sp.dob && (
                                                <p className="text-xs text-gray-500">
                                                  DOB: {sp.dob}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {/* Title, First Name, Last Name */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                              Title *
                            </label>
                            <select
                              value={passenger.title}
                              onChange={(e) => updatePassenger(index, 'title', e.target.value)}
                              data-testid={`pax-${index}-title`}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] bg-white"
                            >
                              {titleOptions.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                              First Name *
                            </label>
                            <input
                              type="text"
                              value={passenger.firstName}
                              onChange={(e) => updatePassenger(index, 'firstName', e.target.value)}
                              placeholder="As per passport/ID"
                              data-testid={`pax-${index}-first-name`}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                              Last Name *
                            </label>
                            <input
                              type="text"
                              value={passenger.lastName}
                              onChange={(e) => updatePassenger(index, 'lastName', e.target.value)}
                              placeholder="As per passport/ID"
                              data-testid={`pax-${index}-last-name`}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                            />
                          </div>
                        </div>
                        
                        {/* Gender and DOB - Responsive */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                              Gender *
                            </label>
                            <select
                              value={passenger.gender}
                              onChange={(e) => updatePassenger(index, 'gender', e.target.value)}
                              data-testid={`pax-${index}-gender`}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] bg-white"
                            >
                              <option value="M">Male</option>
                              <option value="F">Female</option>
                            </select>
                          </div>
                          
                          {/* Date of Birth — show for CHILD/INFANT, international flights,
                              OR when /review.conditions surfaces a per-pax-type DOB
                              requirement (e.g. fares with `pcs.dobe=true` mandate
                              adult DOB too — Audit-P0 R-1 fix, 30-Apr-2026). */}
                          {(passenger.type === 'CHILD'
                            || passenger.type === 'INFANT'
                            || isInternational
                            || (formConditions?.dob_required?.[((passenger.type || 'ADULT').toLowerCase())])) && (
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                value={passenger.dob}
                                onChange={(e) => updatePassenger(index, 'dob', e.target.value)}
                                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] text-sm sm:text-base"
                                max={new Date().toISOString().split('T')[0]}
                                style={{ minHeight: '48px' }}
                                data-testid={`pax-${index}-dob-input`}
                              />
                              {isInternational && (
                                <p className="text-xs text-gray-500 mt-1">Required for international travel</p>
                              )}
                              {!isInternational
                                && passenger.type === 'ADULT'
                                && formConditions?.dob_required?.adult && (
                                <p className="text-xs text-amber-600 mt-1">
                                  Required for this fare
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Passport Details (for international flights) */}
                        {isInternational && (
                          <div className="border-t border-gray-300 pt-4 mt-4">
                            <p className="text-xs font-bold text-gray-700 uppercase mb-3">
                              Passport Details (Required for International)
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Passport Number *
                                </label>
                                <input
                                  type="text"
                                  value={passenger.passportNo}
                                  onChange={(e) => updatePassenger(index, 'passportNo', e.target.value.toUpperCase())}
                                  placeholder="A12345678"
                                  maxLength={15}
                                  data-testid={`pax-${index}-passport-no`}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Nationality *
                                </label>
                                <select
                                  value={passenger.nationality}
                                  onChange={(e) => updatePassenger(index, 'nationality', e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] bg-white"
                                >
                                  <option value="IN">India (IN)</option>
                                  <option value="US">United States (US)</option>
                                  <option value="GB">United Kingdom (GB)</option>
                                  <option value="AE">United Arab Emirates (AE)</option>
                                  <option value="CA">Canada (CA)</option>
                                  <option value="AU">Australia (AU)</option>
                                  <option value="SG">Singapore (SG)</option>
                                  <option value="MY">Malaysia (MY)</option>
                                  <option value="TH">Thailand (TH)</option>
                                  <option value="JP">Japan (JP)</option>
                                  <option value="CN">China (CN)</option>
                                  <option value="FR">France (FR)</option>
                                  <option value="DE">Germany (DE)</option>
                                  <option value="IT">Italy (IT)</option>
                                  <option value="ES">Spain (ES)</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Passport Issue Date
                                </label>
                                <input
                                  type="date"
                                  value={passenger.passportIssueDate}
                                  onChange={(e) => updatePassenger(index, 'passportIssueDate', e.target.value)}
                                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] text-sm sm:text-base"
                                  max={new Date().toISOString().split('T')[0]}
                                  style={{ minHeight: '48px' }}
                                />
                                <p className="text-xs text-gray-500 mt-1">Optional but recommended</p>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Passport Expiry Date *
                                </label>
                                <input
                                  type="date"
                                  value={passenger.passportExpiry}
                                  onChange={(e) => updatePassenger(index, 'passportExpiry', e.target.value)}
                                  data-testid={`pax-${index}-passport-expiry`}
                                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] text-sm sm:text-base"
                                  min={new Date().toISOString().split('T')[0]}
                                  style={{ minHeight: '48px' }}
                                />
                                <p className="text-xs text-gray-500 mt-1">Must be valid for 6+ months</p>
                              </div>
                            </div>
                            
                            {/* Important Notice */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                              <p className="text-xs text-amber-800">
                                <strong>Important:</strong> Ensure passport details match exactly as on your passport. Most countries require passport validity of 6 months beyond travel date.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* T0 · PAN Card (when /review.conditions.ipa=true,
                             v2.0.2). Adults only. Required by Tripjack for
                             high-value / SME / certain corporate fares. */}
                        {panRequired && passenger.type === 'ADULT' && (
                          <div className="border-t border-gray-300 pt-4 mt-4">
                            <p className="text-xs font-bold text-gray-700 uppercase mb-3">
                              PAN Card Details (Required for this fare)
                            </p>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                PAN Number *
                              </label>
                              <input
                                type="text"
                                data-testid={`pan-input-${index}`}
                                value={passenger.pan || ''}
                                onChange={(e) => updatePassenger(index, 'pan', e.target.value.toUpperCase())}
                                placeholder="ABCDE1234F"
                                maxLength={10}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] tracking-wider"
                              />
                              <p className="text-xs text-gray-500 mt-1">10 characters · format: AAAAA9999A</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                              <p className="text-xs text-blue-800">
                                <strong>Why PAN?</strong> The airline requires a PAN for tax compliance on this fare. Your PAN is sent securely to the airline and is not stored beyond the booking.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Contact Details */}
                <div className="border-t-2 border-[#1B375B] pt-6">
                  <h4 className="font-bold text-[#1B375B] mb-4 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="your@email.com"
                        data-testid="booking-contact-email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Booking confirmation will be sent here</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                        Phone Number *
                      </label>
                      <PhoneInput
                        value={contactPhone}
                        onChange={setContactPhone}
                        defaultCountry="IN"
                        placeholder="Mobile number"
                        testid="booking-contact-phone"
                      />
                      <p className="text-xs text-gray-500 mt-1">Select country, we'll send booking updates here</p>
                    </div>
                  </div>
                </div>

                {/* G3 · Emergency Contact — only shown when fare requires it (iecr=true) */}
                {emergencyContactRequired && (
                  <div
                    className="border-t-2 border-amber-400 pt-6 mt-6"
                    data-testid="emergency-contact-section"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-[#1B375B]">Emergency Contact (Required for this fare)</h4>
                        <p className="text-xs text-gray-600 mt-0.5">
                          The airline has asked us to capture an emergency contact for this booking. This person will not receive booking notifications.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Full Name *</label>
                        <input
                          type="text"
                          value={emergencyContact.name}
                          onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                          placeholder="Contact name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
                          data-testid="emergency-contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Email *</label>
                        <input
                          type="email"
                          value={emergencyContact.email}
                          onChange={(e) => setEmergencyContact({ ...emergencyContact, email: e.target.value })}
                          placeholder="emergency@example.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
                          data-testid="emergency-contact-email"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Phone *</label>
                        <PhoneInput
                          value={emergencyContact.phone}
                          onChange={(v) => setEmergencyContact({ ...emergencyContact, phone: v })}
                          defaultCountry="IN"
                          placeholder="Emergency phone"
                          testid="emergency-contact-phone"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* GST Invoice Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">
                    GST Invoice
                    {formConditions?.gst_mandatory && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"
                        data-testid="gst-mandatory-badge"
                      >
                        Required by airline
                      </span>
                    )}
                  </h3>
                  {/* Sprint-3 · SSOT §5.1 — when fare's `gst_mandatory=true`,
                      do NOT expose a toggle. The user must supply GST. */}
                  {!formConditions?.gst_mandatory && (
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gstEnabled}
                        onChange={(e) => setGstEnabled(e.target.checked)}
                        className="w-5 h-5 text-[#659A9B] border-gray-300 rounded focus:ring-[#659A9B] focus:ring-2"
                        data-testid="gst-toggle-checkbox"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Request GST Invoice</span>
                    </label>
                  )}
                </div>
                
                {(gstEnabled || formConditions?.gst_mandatory) && (
                  <div className="space-y-4 animate-fadeIn" data-testid="gst-form-section">
                    <p className="text-xs text-gray-500 mb-4">
                      {formConditions?.gst_mandatory
                        ? 'This airline requires GST details to issue your tax invoice. Please complete the fields below.'
                        : 'Fill GST details to receive a tax invoice for your booking'}
                    </p>
                    
                    {/* GST Number */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                        GST Number (GSTIN) *
                      </label>
                      <input
                        type="text"
                        value={gstDetails.gstNumber}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setGstDetails({ ...gstDetails, gstNumber: value });
                        }}
                        placeholder="29ABCDE1234F1Z5"
                        maxLength={15}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                      />
                      <p className="text-xs text-gray-500 mt-1">15-character GST number</p>
                    </div>
                    
                    {/* Company Name */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                        Registered Company Name *
                      </label>
                      <input
                        type="text"
                        value={gstDetails.registeredName}
                        onChange={(e) => setGstDetails({ ...gstDetails, registeredName: e.target.value })}
                        placeholder="ABC Private Limited"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                      />
                    </div>
                    
                    {/* Email and Mobile in grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                          GST Email *
                        </label>
                        <input
                          type="email"
                          value={gstDetails.email}
                          onChange={(e) => setGstDetails({ ...gstDetails, email: e.target.value })}
                          placeholder="accounts@company.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                          GST Mobile *
                        </label>
                        <PhoneInput
                          value={gstDetails.mobile}
                          onChange={(v) => setGstDetails({ ...gstDetails, mobile: v })}
                          defaultCountry="IN"
                          placeholder="GST contact number"
                          testid="booking-gst-mobile"
                        />
                      </div>
                    </div>
                    
                    {/* Address */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                        Registered Address *
                      </label>
                      <textarea
                        value={gstDetails.address}
                        onChange={(e) => setGstDetails({ ...gstDetails, address: e.target.value })}
                        placeholder="Company registered office address"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] resize-none"
                      />
                    </div>
                    
                    {/* Info Message */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                      <p className="text-xs text-blue-800">
                        <strong>Note:</strong> GST invoice will be issued by ACF Tourism Pvt Ltd. Your GST details will be used for generating the invoice.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Fare Summary */}
          <div className="lg:w-96 flex-shrink-0">
            {/* Compact Countdown Timer - Above Fare Summary */}
            {searchTimestamp && !fareExpired && timeRemaining !== null && (
              <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-gray-700">Fare expires in</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-orange-600">
                    {Math.floor(timeRemaining / 60000)}:{String(Math.floor((timeRemaining % 60000) / 1000)).padStart(2, '0')}
                  </span>
                </div>
              </div>
            )}

            {/* Sprint-3 · /review-on-mount loading indicator. Visible until
                airline requirements (form_conditions) are loaded so the user
                knows passport / DOB / GST fields may still update. */}
            {reviewLoading && (
              <div
                className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2"
                data-testid="review-loading-indicator"
              >
                <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-xs text-blue-800">Loading airline booking requirements…</span>
              </div>
            )}
            {reviewError && !reviewLoading && (
              <div
                className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3"
                data-testid="review-error-indicator"
              >
                <p className="text-xs text-amber-900">
                  <strong>Heads up:</strong> {reviewError}
                </p>
              </div>
            )}
            
            {/* Fare Expired Warning - Compact Version */}
            {fareExpired && (
              <div className="mb-4 bg-red-50 border border-red-300 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-900">Fare Expired</p>
                    <p className="text-xs text-red-700 mt-1">Please search again</p>
                    <button
                      onClick={() => navigate('/')}
                      className="mt-2 w-full px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Search New Flights
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 sticky top-20">
              <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 rounded-t-2xl">
                <h3 className="text-white font-bold">Fare Summary</h3>
              </div>
              
              <div className="p-6 space-y-4">
                {hasFareBreakdown ? (
                  <>
                    {/* Travelers count line — Sprint May-2026 · Issue 2 */}
                    {_paxLine && (
                      <div className="text-xs text-gray-500 pb-2 border-b border-gray-100" data-testid="fare-summary-pax-line">
                        <span className="font-medium text-gray-700">Travelers:</span> {_paxLine}
                      </div>
                    )}
                    <div className="flex justify-between text-sm" data-testid="fare-summary-base-fare-row">
                      <span className="text-gray-600">Base Fare</span>
                      <span className="font-semibold">₹{baseFare.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm" data-testid="fare-summary-taxes-row">
                      <span className="text-gray-600">Taxes & Fees</span>
                      <span className="font-semibold">₹{taxes.toLocaleString()}</span>
                    </div>
                    {/* Sprint Feb-2026 · Service Charge (markup) row.
                        Hidden when effectiveServiceCharge === 0 per
                        product UX call. Falls back to /api/markup/quote
                        for deeplink users (see useEffect above).
                        Sprint May-2026 · Issue 1 — per-pax sub-line. */}
                    {_effectiveServiceCharge > 0 && (
                      <div data-testid="fare-summary-service-charge-row">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Service Charge</span>
                          <span className="font-semibold">₹{_effectiveServiceCharge.toLocaleString()}</span>
                        </div>
                        {_muSubLine && (
                          <p className="text-[11px] text-gray-500 mt-0.5" data-testid="fare-summary-service-charge-breakdown">
                            {_muSubLine}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-500" data-testid="fare-summary-no-breakdown-note">
                    Itemised fare breakdown will appear on your GST invoice.
                  </div>
                )}
                
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-800">
                      {commissionFare.showAmountToPayLabel ? 'Amount to Pay' : 'Total Amount'}
                    </span>
                    <span className="font-bold text-2xl text-[#1B375B]">₹{totalPrice.toLocaleString()}</span>
                  </div>
                </div>
                <FareCommissionRows commissionFare={commissionFare} />
                
                {/* Sprint H1 · Hold-vs-Instant chooser (only shown when fare permits) */}
                <HoldBookingToggle
                  eligible={holdAllowed}
                  value={bookingMode}
                  onChange={setBookingMode}
                  provider="tripjack"
                />

                {/* Error Message */}
                {repriceContinueHint && (
                  <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    {repriceContinueHint}
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2" data-testid="booking-error-banner">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
                  </div>
                )}

                <ReviewMessagesBanner formConditions={formConditions} testIdSuffix="booking-page" />

                {/* U1 + U2 — Pre-empt the "no longer available" dead-end.
                    Render a session-expiry chip when supplier publishes
                    `conditions.st`, and a hard-latched red banner when the
                    reprice flow has exhausted. Both gate the Continue CTA. */}
                {(() => {
                  const _ts = (sessionExpiryStateRef.current || {});
                  return (_ts.hasSupplierTtl || repriceExhausted) ? (
                    <div className="flex flex-col gap-2">
                      {_ts.hasSupplierTtl && (
                        <SessionExpiryChip
                          formConditions={formConditions}
                          onExpire={() => setSessionExpired(true)}
                          testIdSuffix="booking-page"
                        />
                      )}
                      {repriceExhausted && (
                        <div
                          className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
                          data-testid="reprice-exhausted-banner"
                          role="alert"
                        >
                          <span className="font-semibold">Fare locked.</span>{' '}
                          The fare changed too many times. Please search again — this flight cannot be booked at the held price.
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Book Button */}
                <button
                  type="button"
                  data-testid="booking-continue-btn"
                  onClick={handleBooking}
                  disabled={
                    loading
                    || !contactEmail
                    || !contactPhone
                    || passengers.some(p => !p.firstName || !p.lastName)
                    || (isTripjackPaxGate && !passengersUnlocked)
                    || repriceExhausted
                    || sessionExpired
                  }
                  className="w-full py-4 bg-[#1B375B] text-white font-bold rounded-xl hover:bg-[#335E77] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Loading Add-Ons...
                    </>
                  ) : repriceExhausted || sessionExpired ? (
                    <>Please re-search</>
                  ) : (
                    <>{tripjackSkipAddons ? 'Continue to review' : 'Continue to Add-Ons'}</>
                  )}
                </button>
                
                <p className="text-xs text-gray-500 text-center mt-2">
                  {loading
                    ? 'Please wait, fetching baggage options...'
                    : (tripjackSkipAddons
                      ? 'Add-ons are not available on this fare.'
                      : 'Select add-ons and review booking')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
      <BottomNav />
      
      {/* Mobile OTP Login Modal */}
      <MobileOTPLogin 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={(userData) => {
          console.log('Login successful:', userData);
          loadSavedPassengers();
        }}
      />

      <FareRepriceModal
        open={fareRepriceOpen}
        onClose={() => { setFareRepriceOpen(false); setFareRepriceDetail(null); }}
        detail={fareRepriceDetail}
        priceIds={fareRepricePriceIds}
        repriceAttempt={fareRepriceAttempt}
        onRefreshSuccess={handleBookingPageReprice}
        source="booking_page_review"
      />
    </div>
  );
};

export default BookingPage;

